import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import Validator from '../../../../shared/validator';
import Loading from '../../../../shared/loading';
import mutate from '../../../../shared/mutate';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import ConfirmationModal from '../../../shared/confirmation-modal';
import ErrorBoundary from '../../../../shared/error-boundary';
import {
  getById, createOrUpdate, deleteById, archive, unarchive,
} from '../../../../services/lwadmin/series/series-service';

export default class EditSeriesModal extends React.Component {
  static propTypes = {
    seriesDidSave: PropTypes.func,
  }

  static defaultProps = {
    seriesDidSave: null,
  }

  constructor(props, context) {
    super(props, context);

    this.validator = new Validator({
      titleRequired: state => !!state.series.title,
      slugRequired: state => !!state.series.slug,
      descriptionRequired: state => !!state.series.description,
    });
    this.state = {
      series: {
        title: '',
        slug: '',
        description: '',
        archived: false,
      },
      validations: this.validator.getValidations(),
      // eslint-disable-next-line react/no-unused-state
      slugHasBeenManuallyChanged: false,
    };

    this.handleTitleChange = this.handleTitleChange.bind(this);
    this.handleSlugChange = this.handleSlugChange.bind(this);
    this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.deleteSeries = this.deleteSeries.bind(this);
    this.toggleArchive = this.toggleArchive.bind(this);
    this.present = this.present.bind(this);
    // eslint-disable-next-line max-len
    this.presentDeleteSeriesConfirmationModal = this.presentDeleteSeriesConfirmationModal.bind(this);
  }

  componentDidMount() {
    const self = this;

    this.$modal.click(function onClick(e) {
      if (e.target === this) self.dismiss();
    });
  }

  handleTitleChange(e) {
    const title = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.series.title = title;
      state.validations.titleRequiredValid = this.validator.validateTitleRequired(state);

      if (!state.slugHasBeenManuallyChanged) {
        state.series.slug = _.snakeCase(title);
        state.validations.slugRequiredValid = this.validator.validateSlugRequired(state);
      }
    }));
  }

  handleSlugChange(e) {
    const slug = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.slugHasBeenManuallyChanged = !!slug;
      state.series.slug = slug;
      state.validations.slugRequiredValid = this.validator.validateSlugRequired(state);
    }));
  }

  handleDescriptionChange(e) {
    const description = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.series.description = description;
      state.validations.descriptionRequired = this.validator.validateDescriptionRequired(state);
    }));
  }

  save(evt) {
    evt && evt.preventDefault && evt.preventDefault();

    this.setState(
      state => ({
        validations: this.validator.validateForm(state),
      }),
      () => {
        if (!this.validator.formValid) return;

        this.loading.start();
        createOrUpdate(this.state.series)
          .then(() => {
            this.dismiss();
            this.props.seriesDidSave && this.props.seriesDidSave();
          })
          .catch(e => {
            this.loading.stop();
            defaultOnErrorHandler(e, 'Error saving series');
          });
      },
    );
  }

  present(seriesId = null) {
    this.validator.resetValidation();
    this.setState({
      validations: this.validator.getValidations(),
    });
    this.$overlay.fadeIn();
    this.$modal.fadeIn();
    this.$titleInput.focus();

    if (seriesId === null) {
      this.setState({
        series: {
          title: '',
          slug: '',
          description: '',
          archived: false,
        },
        // eslint-disable-next-line react/no-unused-state
        slugHasBeenManuallyChanged: false,
      });
    } else {
      this.loading.start();
      getById(seriesId)
        .then(result => this.setState({
          series: _.pick(result.data, [
            'id', 'title', 'slug', 'description', 'archived', 'number_of_wires',
          ]),
          // eslint-disable-next-line react/no-unused-state
          slugHasBeenManuallyChanged: true,
        }))
        .catch(e => defaultOnErrorHandler(e, 'Error loading series'))
        .finally(() => this.loading.stop());
    }
  }

  dismiss() {
    this.$overlay.fadeOut();
    this.$modal.fadeOut();
  }

  presentDeleteSeriesConfirmationModal() {
    this.deleteSeriesConfirmationModal.present();
  }

  deleteSeries() {
    this.loading.start();

    const numberOfWires = this.state.series.number_of_wires;

    deleteById(this.state.series.id)
      .then(() => {
        this.dismiss();
        this.props.seriesDidSave && this.props.seriesDidSave();
        toastr.clear();
        toastr.success(`Series has been deleted, ${numberOfWires} wires un-assigned`);
      })
      .catch(e => defaultOnErrorHandler(e, 'Error deleting this series'))
      .finally(() => this.loading.stop());
  }

  presentArchiveSeriesConfirmationModal() {
    this.toggleArchiveConfirmationModal.present();
  }

  toggleArchive() {
    this.loading.start();

    const wasArchived = this.state.series.archived;

    (wasArchived ? unarchive : archive)(this.state.series.id)
      .then(() => {
        this.setState(state => {
          state.series.archived = !state.series.archived;
          return state;
        });
        this.dismiss();
        this.props.seriesDidSave && this.props.seriesDidSave();
        toastr.clear();
        toastr.success(`Series has been ${wasArchived ? 'un' : ''}archived`);
      })
      .catch(e => defaultOnErrorHandler(e, `Error ${wasArchived ? 'un' : ''}archiving this series`))
      .finally(() => this.loading.stop());
  }

  render() {
    const action = this.state.series.archived ? 'Unarchive' : 'Archive';
    return (
      <ErrorBoundary>
        <div>
          <div className="modal-overlay" role="region" ref={el => (this.$overlay = $(el))} onClick={this.dismiss} />
          <div className="modal" tabIndex="-1" role="dialog" ref={el => (this.$modal = $(el))}>
            <article>
              <header>
                <h4>
                  <span>
                    {`${this.state.series.id ? 'Editing' : 'Creating'} a series`}
                  </span>
                </h4>
              </header>
              <Loading ref={el => (this.loading = el)}>
                <section>
                  <form>
                    <p className={`form-group ${this.state.validations.titleRequiredValid ? '' : 'has-error'}`}>
                      <label htmlFor="title">
                        Title:
                        <input
                          id="title"
                          type="text"
                          placeholder="Title"
                          value={this.state.series.title}
                          onChange={this.handleTitleChange}
                          ref={el => (this.$titleInput = $(el))}
                        />
                      </label>
                      {this.state.validations.titleRequiredValid || <span className="help-block">Title is required</span>}
                    </p>
                    <p className={`form-group ${this.state.validations.slugRequiredValid ? '' : 'has-error'}`}>
                      <label htmlFor="slug">
                        Slug:
                        <input
                          id="slug"
                          type="text"
                          placeholder="Slug"
                          value={this.state.series.slug}
                          onChange={this.handleSlugChange}
                        />
                      </label>
                      {this.state.validations.slugRequiredValid || <span className="help-block">Slug is required</span>}
                    </p>
                    <p className={`form-group ${this.state.validations.descriptionRequiredValid ? '' : 'has-error'}`}>
                      <label htmlFor="destination">
                        Description:
                        <input
                          id="destination"
                          type="text"
                          placeholder="Description"
                          value={this.state.series.description}
                          onChange={this.handleDescriptionChange}
                        />
                      </label>
                      {this.state.validations.descriptionRequiredValid || <span className="help-block">Description is required</span>}
                    </p>
                  </form>
                </section>
                {!!this.state.series.id && (
                  <div className="form-group">
                    <label htmlFor="number_of_wires">
                      {'Number of assigned wires: '}
                      <span id="number_of_wires">{this.state.series.number_of_wires}</span>
                    </label>
                  </div>
                )}
                <footer>
                  {!!this.state.series.id && (
                    <React.Fragment>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => this.presentDeleteSeriesConfirmationModal()}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="btn --dark"
                        onClick={() => this.presentArchiveSeriesConfirmationModal()}
                      >
                        {action}
                      </button>
                    </React.Fragment>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => this.dismiss()}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={e => this.save(e)}
                  >
                    Save
                  </button>
                </footer>
              </Loading>
            </article>
          </div>
        </div>
        <ConfirmationModal
          ref={el => (this.deleteSeriesConfirmationModal = el)}
          title="Delete series"
          message="Are you sure you want to delete this series?"
          onConfirm={this.deleteSeries}
        />
        <ConfirmationModal
          ref={el => (this.toggleArchiveConfirmationModal = el)}
          title={`${action} series`}
          message={`Are you sure you want to ${_.toLower(action)} this series?`}
          onConfirm={this.toggleArchive}
        />
      </ErrorBoundary>
    );
  }
}
